import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CalendarDays, MapPin, Users, Trophy, Copy, Plus, Settings, Pencil, Home } from 'lucide-react';
import { AuthContext, apiClient } from '../App';
import PlayerFollowButton from '../components/PlayerFollowButton';
import Footer from '../components/Footer';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { resolveAvatarUrl } from '../lib/avatarUrl';
import { getUserTimezone, parseUTC } from '../utils/datetime';
import { useSafeBack } from '../hooks/useSafeBack';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { toast } from 'sonner';

export const ATHLETE_LEVEL_LABELS = {
  youth: 'Youth',
  high_school: 'High School',
  college: 'College',
  pro: 'Pro',
};
export const ABILITY_LEVEL_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

const CONTACT_OR_MARKUP = /<\s*\/?\s*[a-z][^>]*>|\bon[a-z]+\s*=|https?:\/\/|www\.|\b(?:[a-z0-9-]+\.)+(?:com|org|net|io|co|us|me|app|dev)\b|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|(?<!\d)(?:\+?1[\s().-]*)?(?:\(?\d{3}\)?[\s.-]*)\d{3}[\s.-]*\d{4}(?!\d)/i;

export const validateStorefrontForm = (form) => {
  const errors = {};
  const limits = { storefront_headline: 80, storefront_about: 600 };
  Object.entries(limits).forEach(([field, limit]) => {
    const value = form[field] || '';
    if (value.length > limit) errors[field] = `Must be ${limit} characters or less.`;
    else if (CONTACT_OR_MARKUP.test(value)) errors[field] = 'Use plain text without links or contact information.';
  });
  const validateSelections = (field, labels, maximum) => {
    const values = form[field] || [];
    if (values.length > maximum || new Set(values).size !== values.length || values.some(value => !labels[value])) errors[field] = 'Review these selections.';
  };
  validateSelections('storefront_athlete_levels', ATHLETE_LEVEL_LABELS, 4);
  validateSelections('storefront_ability_levels', ABILITY_LEVEL_LABELS, 3);
  return errors;
};

const toStorefrontForm = (trainer) => ({
  storefront_headline: trainer?.storefront_headline || '',
  storefront_about: trainer?.storefront_about || '',
  storefront_athlete_levels: trainer?.storefront_athlete_levels || [],
  storefront_ability_levels: trainer?.storefront_ability_levels || [],
});

const storefrontFormSignature = (value = {}) => JSON.stringify({
  storefront_headline: value.storefront_headline || '',
  storefront_about: value.storefront_about || '',
  storefront_athlete_levels: [...(value.storefront_athlete_levels || [])].sort(),
  storefront_ability_levels: [...(value.storefront_ability_levels || [])].sort(),
});

const storefrontFormsEqual = (left, right) => (
  storefrontFormSignature(left) === storefrontFormSignature(right)
);

const hasStorefrontContent = (trainer) => Boolean(
  trainer.storefront_headline || trainer.storefront_about || trainer.storefront_athlete_levels?.length ||
  trainer.storefront_ability_levels?.length
);

const formatSessionDate = (value) => {
  const date = parseUTC(value);
  if (!date || Number.isNaN(date.getTime())) return 'Date to be announced';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone: getUserTimezone(),
  }).format(date);
};

export const getOwnerVisibilityContent = (visibility = {}) => {
  switch (visibility.status) {
    case 'public': return { title: 'Your trainer page is public', description: 'People can view your public offerings using this link.' };
    case 'needs_public_offering': return { title: 'Your trainer page is not public yet', description: 'Create a public run or host a public club to make your trainer page visible.' };
    case 'private_profile': return { title: 'Your trainer page is not publicly visible', description: 'Your profile is private. Update your privacy settings to make an eligible trainer page public.' };
    case 'teen_not_public': return { title: 'Trainer page preview', description: 'You can preview your hosted activities here.' };
    default: return { title: 'Trainer page preview', description: 'You can preview your trainer storefront here. Public sharing is not available right now.' };
  }
};

const formatSocialCount = (count, label) => `${Number(count || 0).toLocaleString()} ${label}${Number(count || 0) === 1 ? '' : 's'}`;

const joinPreviewNames = (names) => {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const getFollowerSubtext = (person) => (
  person.headline || person.storefront_headline || (person.username ? `@${person.username}` : 'Follower')
);

const getFollowerSocialText = (trainer, people) => {
  const followerCount = Number(trainer.follower_count || 0);
  const mutualCount = Number(trainer.mutual_follower_count || 0);
  const priorityPeople = mutualCount > 0 && people.some(person => person.viewer_is_following)
    ? people.filter(person => person.viewer_is_following)
    : people;
  const names = priorityPeople.slice(0, 2).map(person => person.display_name).filter(Boolean);
  if (names.length === 0) return `${formatSocialCount(followerCount, 'Follower')} follow ${trainer.display_name}`;
  const countBase = mutualCount > 0 ? mutualCount : followerCount;
  const remainder = Math.max(0, countBase - names.length);
  const extra = remainder > 0
    ? ` and ${remainder.toLocaleString()} other${remainder === 1 ? '' : 's'}${mutualCount > 0 ? ' you follow' : ''}`
    : '';
  return mutualCount > 0
    ? `Followed by ${joinPreviewNames(names)}${extra}`
    : `${joinPreviewNames(names)}${extra} follow ${trainer.display_name}`;
};

const FollowerAvatar = ({ person, className = 'h-10 w-10' }) => (
  person.avatar_url
    ? <img src={resolveAvatarUrl(person.avatar_url)} alt="" className={`${className} rounded-full object-cover`} />
    : <div className={`${className} flex items-center justify-center rounded-full bg-zinc-200 text-xs font-semibold text-zinc-700`}>{person.display_name?.charAt(0)}</div>
);

const FollowerPreview = ({ trainer, people, interactive, onOpenFollowers }) => {
  if (!people.length) return null;
  const Container = interactive ? 'button' : 'div';
  const containerProps = interactive
    ? { type: 'button', onClick: onOpenFollowers, 'aria-label': 'View followers' }
    : {};
  return (
    <Container
      {...containerProps}
      className={`w-full rounded-2xl border border-zinc-200 bg-white p-4 text-left shadow-sm ${interactive ? 'transition hover:border-orange-200 hover:bg-orange-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500' : ''}`}
      data-testid="follower-preview"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex shrink-0 -space-x-3">
            {people.slice(0, 5).map(person => (
              <div key={person.id} className="rounded-full border-2 border-white bg-white">
                <FollowerAvatar person={person} />
              </div>
            ))}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Followed by</p>
            <p className="mt-1 text-sm font-semibold text-zinc-900 sm:text-base">{getFollowerSocialText(trainer, people)}</p>
          </div>
        </div>
        <span className="shrink-0 text-sm font-semibold text-orange-700">View Followers</span>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {people.slice(0, 3).map(person => (
          <div key={person.id} className="flex min-w-0 items-center gap-2 rounded-full bg-zinc-50 px-2 py-2">
            <FollowerAvatar person={person} className="h-8 w-8" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zinc-900">{person.display_name}</p>
              <p className="truncate text-xs text-zinc-500">{getFollowerSubtext(person)}</p>
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
};

const TrainerPage = ({ ownerPreview = false }) => {
  const { userId } = useParams();
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const backFallback = ownerPreview ? '/host' : (token ? '/dashboard' : '/');
  const { goBack } = useSafeBack(backFallback);
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [form, setForm] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const loadTrainer = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(ownerPreview ? '/me/trainer-storefront' : `/hosts/${userId}/public`);
      setTrainer(response.data);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
      setTrainer(null);
    } finally { setLoading(false); }
  }, [ownerPreview, token, userId]);

  useEffect(() => { loadTrainer(); }, [loadTrainer]);

  const discardStorefrontEditing = useCallback(() => {
    setEditing(false);
    setFieldErrors({});
    setForm(null);
  }, []);
  const savedStorefrontForm = trainer ? toStorefrontForm(trainer) : null;
  const isStorefrontDirty = Boolean(
    editing && form && savedStorefrontForm && !storefrontFormsEqual(form, savedStorefrontForm)
  );
  const { promptOpen, confirmLeave, cancelLeave, guardedLeave } = useUnsavedChangesGuard({
    active: isStorefrontDirty,
    shouldPrompt: isStorefrontDirty,
  });
  const guardedNavigate = useCallback((to, options) => {
    guardedLeave(() => (options === undefined ? navigate(to) : navigate(to, options)));
  }, [guardedLeave, navigate]);
  const handleBack = useCallback(() => {
    guardedLeave(goBack);
  }, [goBack, guardedLeave]);
  const handleHome = useCallback(() => {
    guardedNavigate(token ? '/dashboard' : '/');
  }, [guardedNavigate, token]);

  useEffect(() => {
    if (!isStorefrontDirty) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isStorefrontDirty]);

  useEffect(() => {
    if (!isStorefrontDirty) return undefined;

    const onDocumentClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || anchor.target && anchor.target !== '_self' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      guardedNavigate(nextPath);
    };

    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [guardedNavigate, isStorefrontDirty]);

  const beginEditing = () => { setForm(toStorefrontForm(trainer)); setFieldErrors({}); setEditing(true); };
  const cancelEditing = () => { guardedLeave(discardStorefrontEditing); };
  const saveStorefront = async () => {
    if (savingRef.current) return;
    const errors = validateStorefrontForm(form);
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    savingRef.current = true;
    setSaving(true);
    setFieldErrors({});
    try {
      const response = await apiClient.patch('/me/trainer-storefront', form);
      setTrainer(response.data);
      discardStorefrontEditing();
      toast.success('Storefront details saved');
    } catch (error) {
      const details = error?.response?.data?.detail;
      const nextErrors = {};
      if (Array.isArray(details)) details.forEach(item => { const field = item?.loc?.at(-1); if (field) nextErrors[field] = item.msg?.replace(/^Value error, /, '') || 'Please review this field.'; });
      setFieldErrors(Object.keys(nextErrors).length ? nextErrors : { form: 'Could not save storefront details. Please try again.' });
    } finally { savingRef.current = false; setSaving(false); }
  };

  const handleAnonymousFollow = () => guardedNavigate('/', { state: { showAuth: true, returnTo: `/host/${userId}` } });
  const copyPublicLink = async () => {
    if (!trainer?.public_visibility?.is_public || !trainer.public_visibility.public_url) return;
    try { await navigator.clipboard.writeText(trainer.public_visibility.public_url); toast.success('Trainer page link copied'); }
    catch { toast.error('Could not copy trainer page link'); }
  };
  const handleFollowChange = (_id, state) => setTrainer(current => ({ ...current, is_following: state.following, request_pending: state.requested, follower_count: current.follower_count + (state.following && !current.is_following ? 1 : (!state.following && current.is_following ? -1 : 0)) }));

  if (loading) return <main className="min-h-screen bg-zinc-50 p-4" aria-busy="true"><div className="mx-auto max-w-5xl animate-pulse rounded-2xl bg-white h-72" /></main>;
  if (unavailable || !trainer) return <main className="min-h-screen bg-zinc-50 px-4 py-16"><Card className="mx-auto max-w-lg text-center"><CardContent className="py-12"><h1 className="text-2xl font-bold">Trainer page not available.</h1><p className="mt-3 text-zinc-600">This trainer storefront cannot be viewed right now.</p><Button className="mt-6" onClick={() => guardedNavigate('/')}>Explore OpenRuns</Button></CardContent></Card></main>;

  const isOwnPage = ownerPreview || user?.id === trainer.id;
  const ownerVisibility = ownerPreview ? getOwnerVisibilityContent(trainer.public_visibility) : null;
  const contentExists = hasStorefrontContent(trainer);
  const visiblePreview = trainer.follower_preview || [];
  const listsAvailable = !ownerPreview || trainer.public_visibility?.is_public;
  const followerPath = `/host/${trainer.id}/followers`;
  const followButtonClass = !trainer.is_following && !trainer.request_pending ? 'border-orange-500 bg-orange-500 text-white hover:border-orange-600 hover:bg-orange-600 hover:text-white' : '';
  const renderFollowControl = () => !isOwnPage && trainer.can_follow !== false && (
    token
      ? <PlayerFollowButton userId={trainer.id} username={trainer.username} isFollowing={trainer.is_following} isRequested={trainer.request_pending} onFollowChange={handleFollowChange} variant="profile" className={followButtonClass} />
      : <Button onClick={handleAnonymousFollow} className="rounded-full bg-orange-500 hover:bg-orange-600">Follow</Button>
  );
  return <div className="min-h-screen overflow-x-hidden bg-zinc-50">
    <main className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBack}
          className="w-fit rounded-full px-3 text-zinc-700 hover:bg-white hover:text-zinc-950 focus-visible:ring-2 focus-visible:ring-orange-500"
          aria-label="Back"
          data-testid="trainer-back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleHome}
          className="h-10 w-10 rounded-full text-zinc-600 hover:bg-white hover:text-zinc-950 focus-visible:ring-2 focus-visible:ring-orange-500"
          aria-label="Home"
          data-testid="trainer-home-btn"
        >
          <Home className="h-4 w-4" />
        </Button>
      </div>

      {ownerPreview && <section className="rounded-2xl border border-orange-200 bg-orange-50 p-5" aria-labelledby="owner-visibility-title" data-testid="owner-visibility-banner"><div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"><div><h2 id="owner-visibility-title" className="text-xl font-bold">{ownerVisibility.title}</h2><p className="mt-1 text-sm text-zinc-700">{ownerVisibility.description}</p></div><div className="flex flex-wrap gap-2">
        {trainer.public_visibility?.is_public && trainer.public_visibility.public_url && <Button type="button" variant="outline" onClick={copyPublicLink} data-testid="copy-public-trainer-link"><Copy className="h-4 w-4" /> Copy public link</Button>}
        <Button type="button" variant="outline" onClick={beginEditing} data-testid="edit-storefront"><Pencil className="h-4 w-4" /> Edit Storefront</Button>
        {['public', 'needs_public_offering'].includes(trainer.public_visibility?.status) && <Button type="button" onClick={() => guardedNavigate('/create-game')} data-testid="owner-create-run"><Plus className="h-4 w-4" /> Create Run</Button>}
        {trainer.public_visibility?.status === 'private_profile' && <Button type="button" onClick={() => guardedNavigate('/profile?edit=privacy')} data-testid="owner-privacy-settings"><Settings className="h-4 w-4" /> Privacy Settings</Button>}
      </div></div></section>}

      {ownerPreview && editing && <Card data-testid="storefront-editor"><CardHeader><CardTitle>Edit storefront details</CardTitle></CardHeader><CardContent className="space-y-5">
        <StorefrontField label="Headline" field="storefront_headline" value={form.storefront_headline} limit={80} error={fieldErrors.storefront_headline} onChange={value => setForm(current => ({ ...current, storefront_headline: value }))} />
        <StorefrontField label="About this training" field="storefront_about" value={form.storefront_about} limit={600} multiline error={fieldErrors.storefront_about} onChange={value => setForm(current => ({ ...current, storefront_about: value }))} />
        <SelectionGroup legend="Athletes I Train" field="storefront_athlete_levels" labels={ATHLETE_LEVEL_LABELS} selected={form.storefront_athlete_levels} error={fieldErrors.storefront_athlete_levels} onChange={values => setForm(current => ({ ...current, storefront_athlete_levels: values }))} />
        <SelectionGroup legend="Ability Levels" field="storefront_ability_levels" labels={ABILITY_LEVEL_LABELS} selected={form.storefront_ability_levels} error={fieldErrors.storefront_ability_levels} onChange={values => setForm(current => ({ ...current, storefront_ability_levels: values }))} />
        {fieldErrors.form && <p role="alert" className="text-sm text-red-700">{fieldErrors.form}</p>}
        <div className="flex flex-wrap gap-2"><Button type="button" onClick={saveStorefront} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button><Button type="button" variant="outline" onClick={cancelEditing} disabled={saving}>Cancel</Button></div>
      </CardContent></Card>}

      {ownerPreview && !editing && !contentExists && <Card className="border-dashed border-orange-300" data-testid="empty-storefront-prompt"><CardContent className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between"><div><h2 className="font-bold">Tell visitors what your training is about.</h2><p className="text-sm text-zinc-600">Add a headline, description, and the athletes you train.</p></div><Button type="button" onClick={beginEditing}>Add Storefront Details</Button></CardContent></Card>}

      <section className="rounded-3xl bg-zinc-950 px-5 py-8 text-white sm:px-8" aria-labelledby="trainer-headline"><div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        {trainer.avatar_url ? <img src={resolveAvatarUrl(trainer.avatar_url)} alt="" className="h-24 w-24 rounded-full object-cover ring-4 ring-orange-500/70" /> : <div className="flex h-24 w-24 items-center justify-center rounded-full bg-orange-500 text-4xl font-bold">{trainer.display_name.charAt(0)}</div>}
        <div className="min-w-0 flex-1"><h1 id="trainer-headline" className="break-words text-3xl font-bold sm:text-4xl">{trainer.storefront_headline || `Training with ${trainer.display_name}`}</h1><p className="mt-2 text-lg text-zinc-300">Training with {trainer.display_name}</p>{trainer.username && <p className="text-sm text-zinc-400">@{trainer.username}</p>}<div className="mt-4 flex flex-wrap items-center gap-2 text-sm font-semibold text-zinc-100">{listsAvailable ? <button type="button" className="rounded-full bg-white/10 px-3 py-1.5 transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400" onClick={() => guardedNavigate(followerPath)}>{formatSocialCount(trainer.follower_count, 'Follower')}</button> : <span className="rounded-full bg-white/10 px-3 py-1.5">{formatSocialCount(trainer.follower_count, 'Follower')}</span>}{listsAvailable ? <button type="button" className="rounded-full bg-white/10 px-3 py-1.5 transition hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400" onClick={() => guardedNavigate(`/host/${trainer.id}/following`)}>{trainer.following_count} Following</button> : <span className="rounded-full bg-white/10 px-3 py-1.5">{trainer.following_count} Following</span>}</div>{(trainer.city || trainer.state) && <p className="mt-3 flex items-center gap-2 text-sm text-zinc-300"><MapPin className="h-4 w-4" />{[trainer.city, trainer.state].filter(Boolean).join(', ')}</p>}</div>
        {renderFollowControl()}
      </div></section>

      <FollowerPreview trainer={trainer} people={visiblePreview} interactive={listsAvailable} onOpenFollowers={() => guardedNavigate(followerPath)} />

      <section aria-labelledby="upcoming-heading" data-testid="booking-section"><div className="mb-4 flex flex-wrap items-end justify-between gap-3"><div><p className="text-sm font-semibold uppercase tracking-wide text-orange-700">Book your next session</p><h2 id="upcoming-heading" className="text-2xl font-bold">Upcoming Training</h2></div>{ownerPreview && <Button type="button" variant="outline" onClick={() => guardedNavigate('/create-game')}><Plus className="h-4 w-4" /> Create Run</Button>}</div>
        {trainer.upcoming_sessions.length === 0 ? <Card data-testid="training-empty-state"><CardContent className="flex flex-col gap-4 py-8 text-zinc-700 sm:flex-row sm:items-center sm:justify-between">{isOwnPage ? <><div><h3 className="font-bold text-zinc-950">Your next public training session will appear here.</h3><p className="mt-1 text-sm">Create one so your followers can discover it.</p></div><Button type="button" onClick={() => guardedNavigate('/create-game')}><Plus className="h-4 w-4" /> Create Training</Button></> : <><div><h3 className="font-bold text-zinc-950">No upcoming training scheduled.</h3><p className="mt-1 text-sm">Follow this trainer and you'll see future public training sessions as soon as they're announced.</p></div><div className="flex flex-wrap gap-2">{renderFollowControl()}{listsAvailable && <Button type="button" variant="outline" onClick={() => guardedNavigate(followerPath)}>View Followers</Button>}</div></>}</CardContent></Card> : <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{trainer.upcoming_sessions.map(session => <Card key={session.id} className="min-w-0"><CardContent className="space-y-3 pt-5"><div className="flex items-start justify-between gap-2"><h3 className="min-w-0 break-words font-semibold">{session.title}</h3><Badge variant="outline" className="shrink-0 capitalize">{session.session_type}</Badge></div><p className="flex items-center gap-2 text-sm text-zinc-600"><CalendarDays className="h-4 w-4 shrink-0" />{formatSessionDate(session.date_time)}</p><p className="flex items-center gap-2 text-sm text-zinc-600"><MapPin className="h-4 w-4 shrink-0" /><span className="truncate">{session.public_location_label}</span></p>{session.is_full === true ? <p className="text-sm font-semibold text-zinc-700">Full</p> : Number.isInteger(session.remaining_spots) && <p className="text-sm font-semibold text-zinc-700">{session.remaining_spots} spot{session.remaining_spots === 1 ? '' : 's'} available</p>}<Button asChild className="w-full"><Link to={session.view_path}>View session</Link></Button></CardContent></Card>)}</div>}
      </section>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(260px,1fr)]"><div className="space-y-8 min-w-0">
        {trainer.storefront_about && <section aria-labelledby="about-training"><h2 id="about-training" className="mb-3 text-2xl font-bold">About this training</h2><p className="whitespace-pre-wrap break-words text-zinc-700">{trainer.storefront_about}</p></section>}
        {(trainer.storefront_athlete_levels?.length > 0 || trainer.storefront_ability_levels?.length > 0) && <section className="grid gap-4 sm:grid-cols-2" aria-label="Training audiences"><SelectionDisplay title="Athletes I Train" values={trainer.storefront_athlete_levels} labels={ATHLETE_LEVEL_LABELS} /><SelectionDisplay title="Ability Levels" values={trainer.storefront_ability_levels} labels={ABILITY_LEVEL_LABELS} /></section>}
      </div><aside className="space-y-6">{trainer.bio && <Card><CardHeader><CardTitle>About the trainer</CardTitle></CardHeader><CardContent><p className="whitespace-pre-wrap break-words text-zinc-700">{trainer.bio}</p></CardContent></Card>}<Card><CardHeader><CardTitle>Trainer at a glance</CardTitle></CardHeader><CardContent className="grid grid-cols-2 gap-4 text-center">{[['Followers', trainer.follower_count, Users], ['Sessions hosted', trainer.hosted_session_count, Trophy]].map(([label, value, Icon]) => <div key={label} className="rounded-xl bg-zinc-50 p-3"><Icon className="mx-auto mb-1 h-5 w-5 text-orange-600" /><div className="text-xl font-bold">{value}</div><div className="text-xs text-zinc-600">{label}</div></div>)}{trainer.member_since && <p className="col-span-2 text-xs text-zinc-500">Member since {new Date(trainer.member_since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</p>}</CardContent></Card></aside></div>
    </main><Footer variant="slim" />
    <DiscardChangesDialog
      open={promptOpen}
      onKeepEditing={cancelLeave}
      onDiscard={confirmLeave}
      title="Discard your changes?"
      description="You have unsaved changes to your trainer storefront."
      keepEditingLabel="Stay Editing"
      discardLabel="Discard Changes"
    />
  </div>;
};

const StorefrontField = ({ label, field, value, limit, multiline = false, error, onChange }) => <div><div className="flex justify-between gap-3"><label htmlFor={field} className="text-sm font-semibold">{label}</label><span className="text-xs text-zinc-500">{value.length}/{limit}</span></div>{multiline ? <textarea id={field} value={value} maxLength={limit} rows={5} onChange={event => onChange(event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${field}-error` : undefined} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" /> : <input id={field} value={value} maxLength={limit} onChange={event => onChange(event.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? `${field}-error` : undefined} className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" />}{error && <p id={`${field}-error`} role="alert" className="mt-1 text-sm text-red-700">{error}</p>}</div>;

const SelectionGroup = ({ legend, field, labels, selected, error, onChange }) => <fieldset><legend className="text-sm font-semibold">{legend}</legend><div className="mt-2 flex flex-wrap gap-2">{Object.entries(labels).map(([value, label]) => { const checked = selected.includes(value); return <label key={value} className={`cursor-pointer rounded-full border px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-orange-500 ${checked ? 'border-orange-600 bg-orange-50 text-orange-900' : 'border-zinc-300 bg-white text-zinc-700'}`}><input type="checkbox" name={field} value={value} checked={checked} onChange={() => onChange(checked ? selected.filter(item => item !== value) : [...selected, value])} className="sr-only" />{label}</label>; })}</div>{error && <p role="alert" className="mt-1 text-sm text-red-700">{error}</p>}</fieldset>;

const SelectionDisplay = ({ title, values = [], labels }) => values.length > 0 ? <Card><CardHeader><CardTitle>{title}</CardTitle></CardHeader><CardContent><div className="flex flex-wrap gap-2">{values.map(value => <Badge key={value} variant="outline">{labels[value]}</Badge>)}</div></CardContent></Card> : null;

export default TrainerPage;
